import React from 'react';
import { Icon } from '@popmenu/common-ui';
import { Help, MinusCircle, PlusCircle, Face, X as XIcon } from '@popmenu/web-icons';
import PropTypes from 'prop-types';
import { InputAdornment as MuiInputAdornment, TextField as MuiTextField, Popover as MuiPopover, Tooltip as MuiTooltip, IconButton } from '@material-ui/core';
import { withIntl } from '../../../utils/withIntl';
import { classNames, withStyles } from '../../../utils/withStyles';
import textFieldStyles from '../../../assets/jss/shared/textFieldStyles';
import Grid from '../../../shared/Grid';
import withTextFieldDebouncer from './withTextFieldDebouncer';

// Prevent server-side error when importing DOM-dependent libraries
let EmojiPicker = null;

class TextField extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      renderEmojiPicker: false,
    };
    this.appendValue = this.appendValue.bind(this);
    this.closeEmojiPopover = this.closeEmojiPopover.bind(this);
    this.handleEmojiClick = this.handleEmojiClick.bind(this);
    this.openEmojiPopover = this.openEmojiPopover.bind(this);
    this.internalRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.emojiPicker) {
      import(/* webpackChunkName: "emoji-picker-react" */ 'emoji-picker-react')
        .then(({ default: emojiPickerReact }) => {
          EmojiPicker = emojiPickerReact;
          this.setState({ renderEmojiPicker: true });
        });
    }
  }

  get inputRef() {
    return this.props.inputRef || this.internalRef;
  }

  openEmojiPopover(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  closeEmojiPopover() {
    this.setState({
      anchorEl: null,
    });
  }

  handleEmojiClick(name, data) {
    this.appendValue(data.emoji);
    this.closeEmojiPopover();
  }

  appendValue(value) {
    if (this.inputRef.current) {
      const lastValue = this.inputRef.current.value;
      if (lastValue) {
        this.inputRef.current.value = `${lastValue} ${value}`;
      } else {
        this.inputRef.current.value = value;
      }
      const event = new Event('input', { bubbles: true });
      event.simulated = true;
      if (this.inputRef.current._valueTracker) {
        this.inputRef.current._valueTracker.setValue(lastValue);
      }
      this.inputRef.current.dispatchEvent(event);
    }
  }

  render() {
    const {
      autoComplete,
      characterLimit,
      classes,
      className,
      color,
      debouncingSafetyAssured,
      disabled,
      emojiPicker,
      endAdornments: endAdornmentsProp,
      field,
      InputProps,
      inputProps,
      intl,
      isClearable,
      isRequiredTitleLabel,
      label,
      maxValue,
      onClear,
      onIncrementDown,
      onIncrementUp,
      placeholder,
      restaurant,
      rows,
      showSteppers,
      showSteppersOnly,
      startIcon,
      step,
      style,
      t,
      title,
      titleTooltip,
      touchDelay,
      variant,
      ...textFieldProps
    } = this.props;
    const endAdornments = [...(endAdornmentsProp || [])];
    const open = Boolean(this.state.anchorEl);

    const dataCySuffix =
      typeof (title || placeholder || '') === 'string' &&
      (title || placeholder || '').toLowerCase() || '';

    // Start Adornment
    let startAdornment = null;
    if (startIcon) {
      startAdornment = (
        <MuiInputAdornment position="start">
          {typeof startIcon === 'string' ? <Icon icon={startIcon} /> : React.createElement(startIcon)}
        </MuiInputAdornment>
      );
    }

    // End Adornment
    let endAdornment = null;
    // Tooltip
    if (titleTooltip) {
      endAdornment = (
        <MuiInputAdornment position="end">
          <MuiTooltip placement="top" title={titleTooltip} enterTouchDelay={touchDelay}>
            <div>
              <Icon icon={Help} />
            </div>
          </MuiTooltip>
        </MuiInputAdornment>
      );
    }

    // Number steppers
    if (textFieldProps.type === 'number' && (showSteppers || showSteppersOnly)) {
      const minusCircleButton = (
        <IconButton
          aria-label={`Remove 1 ${title || placeholder || ''}`}
          color="inherit"
          onClick={onIncrementDown}
          data-cy={`remove_1_${dataCySuffix}`}
        >
          <Icon icon={MinusCircle} />
        </IconButton>
      );

      const plusCircleButton = (
        <IconButton
          disabled={maxValue === parseInt(textFieldProps.value, 10)}
          aria-label={`Add 1 ${title || placeholder || ''}`}
          color="inherit"
          onClick={onIncrementUp}
          data-cy={`add_1_${dataCySuffix}`}
        >
          <Icon icon={PlusCircle} />
        </IconButton>
      );

      if (showSteppersOnly) {
        startAdornment = (
          <MuiInputAdornment position="start">
            {minusCircleButton}
          </MuiInputAdornment>

        );
        endAdornment = (
          <MuiInputAdornment position="end">
            {plusCircleButton}
          </MuiInputAdornment>
        );
      } else {
        endAdornment = (
          <MuiInputAdornment position="end">
            <div className={classes.stepperControls}>
              {minusCircleButton}
              &nbsp;
              {plusCircleButton}
            </div>
          </MuiInputAdornment>
        );
      }
    }

    // Emoji picker
    if (emojiPicker && this.state.renderEmojiPicker) {
      endAdornments.unshift(
        <React.Fragment>
          <Icon
            icon={Face}
            onClick={this.openEmojiPopover}
            style={{ cursor: 'pointer' }}
            data-cy="emoji_picker_button"
          />
          <MuiPopover
            id="simple-popper"
            open={open}
            anchorEl={this.state.anchorEl}
            onClose={this.closeEmojiPopover}
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
            transformOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
          >
            <EmojiPicker onEmojiClick={this.handleEmojiClick} />
          </MuiPopover>
        </React.Fragment>,
      );
    }

    if (endAdornments.length > 0) {
      endAdornment = (
        <MuiInputAdornment position="end">
          <Grid container justifyContent="center" alignItems="center" spacing={0} className={this.props.classes.endAdornmentContainer}>
            {endAdornments.map((adornment, i) => (
              <Grid key={i} item>
                {adornment}
              </Grid>
            ))}
          </Grid>
        </MuiInputAdornment>
      );
    }

    // Clear X
    if (isClearable && textFieldProps.value) {
      endAdornment = (
        <MuiInputAdornment position="end">
          <IconButton
            aria-label={'Clear input'}
            color="inherit"
            onClick={onClear}
          >
            <Icon icon={XIcon} />
          </IconButton>
        </MuiInputAdornment>
      );
    }

    // Convert rows to min-height for textarea
    const inputStyle = { ...style };
    if (this.props.multiline && rows > 1) {
      inputStyle.minHeight = `${rows * 16}px`;
    }

    // Ensure only strings are passed to textTitle which
    // is used for the aria-label on the input element
    let textTitle;
    if (title && typeof title === 'string') {
      textTitle = title;
    } else if (label && typeof label === 'string') {
      textTitle = label;
    }

    // Required field suffix
    if (textTitle && isRequiredTitleLabel) {
      const required = intl.formatMessage({ defaultMessage: 'Required' });
      textTitle = `${title} (${required})`;
    }

    const fieldId = this.props.id || field;

    /**
     * mui text field automatically links the label and input elements if there is an id provided.
     * Adding aria-label in addition to the linked input/label is improper for accessibility because it creates redundant labels.
     * redundant screen reader information can create confusion and is considered an accessibility violation.
     * when determining how to provide screen reader information, prefer native behavior like linking label and input over using aria attributres.
     *
     * removing redundant screen reader information is an improvement, however a lot of our cypress tests rely on the implementation of aria-label.
     * ideally, we would have test steps that are less implementation specific, but for now the dedupeAriaLabel prop is used target specific cases where we can apply this improvement.
     */
    const muiTextFieldLabelProp = !showSteppersOnly ? (textTitle || title || label || undefined) : undefined;
    const ariaLabel = this.props['aria-label'] || textTitle || placeholder;
    const includeAriaLabel = Boolean(!muiTextFieldLabelProp || !fieldId) && Boolean(ariaLabel);
    return (
      <MuiTextField
        autoComplete="off"
        className={classNames(
          className,
          classes.textField,
          (showSteppers || showSteppersOnly) && classes.textfieldWithoutUpDownButtons,
          showSteppersOnly && classes.textFieldWithStepperOnly,
        )}
        disabled={disabled}
        id={fieldId}
        InputLabelProps={{
          htmlFor: fieldId,
        }}
        inputProps={{
          ...((includeAriaLabel || !this.props.dedupeAriaLabel) ? { 'aria-label': ariaLabel } : {}),
          'aria-required': isRequiredTitleLabel || undefined,
          autoComplete,
          className: showSteppersOnly && this.props.classes.centerNumberInField,
          'data-cy': `textfield_${dataCySuffix}`,
          'data-testid': `textfield_${dataCySuffix}`,
          maxLength: characterLimit,
          readOnly: showSteppersOnly,
          step,
          style: inputStyle,
          ...inputProps,
          // id must come after spread to ensure it's not overridden
          id: fieldId,
        }}
        InputProps={{
          endAdornment,
          startAdornment,
          ...InputProps,
        }}
        inputRef={this.inputRef}
        label={!showSteppersOnly ? (textTitle || title || label || undefined) : undefined}
        name={field}
        placeholder={placeholder}
        variant={showSteppersOnly ? 'standard' : variant}
        {...textFieldProps}
      />
    );
  }
}

TextField.defaultProps = {
  'aria-label': null,
  autoComplete: 'off',
  characterLimit: undefined,
  className: null,
  debouncingSafetyAssured: false,
  disabled: false,
  emojiPicker: false,
  endAdornments: null,
  field: null,
  id: null,
  InputProps: {},
  inputProps: {},
  inputRef: null,
  isClearable: false,
  isRequiredTitleLabel: false,
  label: undefined,
  maxValue: undefined, // For number steppers
  multiline: false,
  onClear: null,
  onIncrementDown: null,
  onIncrementUp: null,
  placeholder: null,
  restaurant: null,
  rows: 6,
  showSteppers: false,
  showSteppersOnly: false,
  startIcon: null,
  step: null,
  style: {},
  title: null,
  titleTooltip: null,
  touchDelay: null,
  type: 'text',
  value: undefined,
  variant: 'outlined',
};

TextField.propTypes = {
  'aria-label': PropTypes.string,
  autoComplete: PropTypes.string,
  characterLimit: PropTypes.number,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  debouncingSafetyAssured: PropTypes.bool,
  disabled: PropTypes.bool,
  emojiPicker: PropTypes.bool,
  endAdornments: PropTypes.arrayOf(PropTypes.node),
  field: PropTypes.string,
  id: PropTypes.string,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  inputRef: PropTypes.object,
  isClearable: PropTypes.bool,
  isRequiredTitleLabel: PropTypes.bool,
  label: PropTypes.string,
  maxValue: PropTypes.number,
  multiline: PropTypes.bool,
  onClear: PropTypes.func,
  onIncrementDown: PropTypes.func,
  onIncrementUp: PropTypes.func,
  placeholder: PropTypes.string,
  restaurant: PropTypes.object,
  rows: PropTypes.number,
  showSteppers: PropTypes.bool,
  showSteppersOnly: PropTypes.bool,
  startIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  step: PropTypes.string,
  style: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  titleTooltip: PropTypes.string,
  touchDelay: PropTypes.number,
  type: PropTypes.string,
  value: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
};

export default withTextFieldDebouncer(withStyles(textFieldStyles)(withIntl(TextField)));
